// ═══════════════════════════════════════════════════════════════
// MailingPage.js — Module Mailing & Newsletter
// ═══════════════════════════════════════════════════════════════
// Sessions J/J+1 : connecteur listmonk, composer IA, bouton chat→mailing.
// Session J+1 (suite) : harmonisation visuelle avec le langage Coffre-fort
//   · CSS vars globales (theme-aware sans useTheme)
//   · violet doux outline (accent #a78bfa) au lieu de violet plein criard
//   · header Coffre-style + encart pédagogique
//   · cards aérées au lieu de lignes serrées
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Mail, Users, Send, Gauge, Plus, Trash2, RefreshCw, Search, ArrowLeft, X, ShieldCheck, Upload, Download, Check, Filter, Pencil, History, AlertCircle } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';
import * as mailingApi from '../../services/mailingApi';
import { MailingApiError } from '../../services/mailingApi';
import MailingComposerModal from './MailingComposerModal';
import BulkImportModal from './BulkImportModal';

const TAB_DEF = [
    { key: 'lists', label: 'Mes listes', icon: Mail },
    { key: 'campaigns', label: 'Campagnes', icon: Send },
    { key: 'quotas', label: 'Quotas', icon: Gauge },
];

// Palette violet doux figée (mirroirs VaultPage), CSS vars pour le reste.
const palette = {
    bg: 'var(--bg)',
    cardBg: 'var(--card-bg)',
    cardBorder: 'var(--card-border)',
    text: 'var(--text)',
    muted: 'var(--text-secondary)',
    accent: '#a78bfa',
    accentBg: 'rgba(167,139,250,0.12)',
    accentSoft: 'rgba(167,139,250,0.05)',
    accentBorder: 'rgba(167,139,250,0.30)',
    success: '#22c55e',
    successBg: 'rgba(34,197,94,0.12)',
    warning: '#fbbf24',
    warningBg: 'rgba(251,191,36,0.13)',
    danger: '#ef4444',
    dangerBg: 'rgba(239,68,68,0.10)',
};

function MailingPage({ user }) {
    const [activeTab, setActiveTab] = useState('lists');
    const [openList, setOpenList] = useState(null); // { id, name } | null
    const [composerOpen, setComposerOpen] = useState(false);
    const [composerInitial, setComposerInitial] = useState(null);
    // Tick incrémenté à chaque retour depuis SubscribersTab pour forcer
    // ListsTab à re-fetch les compteurs (qui ont pu bouger pendant l'édition).
    const [listsRefreshTick, setListsRefreshTick] = useState(0);

    return (
        <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
            <style>{`
                .mailing-row-trash { opacity: 0; transition: opacity 0.15s ease; pointer-events: none; }
                .mailing-row-host:hover .mailing-row-trash,
                .mailing-row-host:focus-within .mailing-row-trash { opacity: 1; pointer-events: auto; }
            `}</style>
            <div style={{ padding: '32px 40px 56px 40px', maxWidth: 1100, margin: '0 auto' }}>
                {/* Header — pattern Coffre */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 10,
                        background: 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(192,132,252,0.10))',
                        border: `1px solid ${palette.accentBorder}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Mail size={20} style={{ color: palette.accent }} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: palette.text }}>
                            Mailing & Newsletter
                        </h1>
                        <div style={{ fontSize: 12, color: palette.muted, marginTop: 2 }}>
                            Vos listes de contacts, vos campagnes, vos statistiques.
                        </div>
                    </div>
                </div>

                {/* Tabs — pattern Coffre */}
                {!openList && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 18, borderBottom: `1px solid ${palette.cardBorder}` }}>
                        {TAB_DEF.map(({ key, label, icon: Icon }) => {
                            const active = activeTab === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setActiveTab(key)}
                                    style={{
                                        background: active ? palette.accentBg : 'transparent',
                                        border: 'none',
                                        borderBottom: active ? `2px solid ${palette.accent}` : '2px solid transparent',
                                        color: active ? palette.accent : palette.muted,
                                        padding: '8px 14px', cursor: 'pointer',
                                        fontSize: 12.5, fontWeight: 500,
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    <Icon size={13} /> {label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Bandeau pédagogique — pattern Coffre */}
                {!openList && activeTab === 'lists' && (
                    <div style={{
                        marginTop: 22, marginBottom: 4,
                        background: palette.accentSoft,
                        border: `1px solid ${palette.accentBorder}`,
                        borderRadius: 8, padding: '12px 14px',
                        fontSize: 12.5, lineHeight: 1.55, color: palette.muted,
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                    }}>
                        <ShieldCheck size={15} style={{ color: palette.accent, flexShrink: 0, marginTop: 1 }} />
                        <div>
                            <strong style={{ color: palette.accent }}>Vos listes, votre voix.</strong>{' '}
                            Hébergées sur l'infra ITRIIA, conformes RGPD, double opt-in par défaut.
                            Chaque envoi est tracé dans Campagnes pour vous protéger d'un usage non sollicité.
                        </div>
                    </div>
                )}

                <div style={{ marginTop: 22 }}>
                    {openList ? (
                        <SubscribersTab
                            listId={openList.id}
                            listName={openList.name}
                            onBack={() => {
                                setOpenList(null);
                                setListsRefreshTick((t) => t + 1);
                            }}
                        />
                    ) : activeTab === 'lists' ? (
                        <ListsTab
                            refreshTick={listsRefreshTick}
                            onOpenList={(id, name) => setOpenList({ id, name })}
                            onCompose={(initial) => { setComposerInitial(initial); setComposerOpen(true); }}
                        />
                    ) : activeTab === 'campaigns' ? (
                        <CampaignsTab onCompose={(initial) => { setComposerInitial(initial); setComposerOpen(true); }} />
                    ) : (
                        <QuotasTab />
                    )}
                </div>

                {composerOpen && (
                    <MailingComposerModal
                        initial={composerInitial}
                        onClose={() => { setComposerOpen(false); setComposerInitial(null); }}
                        onSent={() => {
                            setComposerOpen(false);
                            setComposerInitial(null);
                            setActiveTab('campaigns');
                            toast.success('Campagne envoyée — les statistiques arriveront sous peu.');
                        }}
                    />
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// Onglet Listes
// ═══════════════════════════════════════════════════════════════
function ListsTab({ refreshTick = 0, onOpenList, onCompose }) {
    const [editingList, setEditingList] = useState(null);

    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { lists } = await mailingApi.listLists();
            setLists(lists || []);
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Re-fetch au mount + à chaque retour depuis SubscribersTab (refreshTick).
    useEffect(() => { load(); }, [load, refreshTick]);

    const handleDelete = async (list) => {
        const ok = await confirmModal(
            `Supprimer la liste « ${list.name} » ? Les contacts ne seront plus sollicités. Cette action est irréversible côté ITRIIA.`,
            { confirmLabel: 'Supprimer', danger: true }
        );
        if (!ok) return;
        try {
            await mailingApi.deleteList(list.listmonk_list_id);
            toast.success('Liste supprimée.');
            load();
        } catch (err) {
            handleApiError(err);
        }
    };

    return (
        <div>
            <div style={sectionHeader}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: palette.text }}>
                    Mes listes <span style={{ color: palette.muted, fontWeight: 500 }}>({lists.length})</span>
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={load} style={iconBtn} title="Rafraîchir">
                        <RefreshCw size={13} />
                    </button>
                    <button onClick={() => setShowCreate(true)} style={accentBtn}>
                        <Plus size={13} /> Nouvelle liste
                    </button>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : lists.length === 0 ? (
                <EmptyState
                    title="Vous n'avez pas encore de liste"
                    cta="Créer une première liste"
                    onCta={() => setShowCreate(true)}
                />
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                    {lists.map((l) => (
                        <article key={l.id} className="mailing-row-host" style={{
                            background: palette.cardBg, border: `1px solid ${palette.cardBorder}`,
                            borderRadius: 12, padding: 18,
                            display: 'flex', flexDirection: 'column', gap: 10,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    background: palette.accentBg,
                                    border: `1px solid ${palette.accentBorder}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Mail size={14} style={{ color: palette.accent }} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: palette.text, flex: 1, minWidth: 0 }}>
                                    {l.name}
                                </h3>
                                <button
                                    onClick={() => setEditingList(l)}
                                    className="mailing-row-trash"
                                    style={{ ...iconBtn, padding: 6, color: palette.accent, borderColor: 'transparent' }}
                                    title="Renommer"
                                >
                                    <Pencil size={13} />
                                </button>
                                <button
                                    onClick={() => handleDelete(l)}
                                    className="mailing-row-trash"
                                    style={{ ...iconBtn, padding: 6, color: palette.danger, borderColor: 'transparent' }}
                                    title="Supprimer"
                                >
                                    <Trash2 size={13} />
                                </button>
                            </div>
                            {l.description && (
                                <p style={{ margin: 0, fontSize: 12.5, color: palette.muted, lineHeight: 1.55 }}>
                                    {l.description}
                                </p>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: palette.muted, marginTop: 'auto' }}>
                                <Users size={12} />
                                <span>{l.subscriber_count || 0} abonné{(l.subscriber_count || 0) > 1 ? 's' : ''}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                <button onClick={() => onOpenList(l.listmonk_list_id, l.name)} style={{ ...accentBtn, flex: 1, justifyContent: 'center' }}>
                                    <Users size={12} /> Contacts
                                </button>
                                <button onClick={() => onCompose({ listmonkListId: l.listmonk_list_id, listName: l.name })} style={{ ...accentBtn, flex: 1, justifyContent: 'center' }}>
                                    <Send size={12} /> Composer
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {showCreate && (
                <CreateListModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
            )}

            {editingList && (
                <EditListModal
                    list={editingList}
                    onClose={() => setEditingList(null)}
                    onSaved={() => { setEditingList(null); load(); }}
                />
            )}
        </div>
    );
}

// Modale d'édition titre/description d'une liste — premium violet
function EditListModal({ list, onClose, onSaved }) {
    const [name, setName] = useState(list.name || '');
    const [description, setDescription] = useState(list.description || '');
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        const cleanName = name.trim();
        if (!cleanName) {
            toast.warning('Le nom ne peut pas être vide.');
            return;
        }
        setSubmitting(true);
        try {
            await mailingApi.updateList(list.listmonk_list_id, {
                name: cleanName,
                description: description.trim(),
            });
            toast.success('Liste renommée.');
            onSaved();
        } catch (err) {
            handleApiError(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalShell title="Renommer la liste" onClose={onClose}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="Nom de la liste">
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && name.trim() && !submitting) submit(); }}
                        placeholder="Newsletter Fondateurs"
                        style={inputStyle}
                        autoFocus
                        maxLength={120}
                    />
                </Field>
                <Field label="Description (optionnelle)">
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="À qui s'adresse cette liste ?"
                        rows={3}
                        style={{ ...inputStyle, resize: 'vertical' }}
                        maxLength={500}
                    />
                </Field>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                    <button onClick={onClose} style={ghostBtn} disabled={submitting}>Annuler</button>
                    <button onClick={submit} style={accentBtn} disabled={submitting || !name.trim()}>
                        {submitting ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

function CreateListModal({ onClose, onCreated }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!name.trim()) {
            toast.warning('Donnez un nom à votre liste.');
            return;
        }
        if (!consent) {
            toast.warning('Le consentement RGPD est obligatoire pour créer une liste.');
            return;
        }
        setSubmitting(true);
        try {
            await mailingApi.createList({
                name: name.trim(),
                description: description.trim() || null,
                consentSigned: true,
            });
            toast.success('Liste créée.');
            onCreated();
        } catch (err) {
            handleApiError(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalShell title="Nouvelle liste" onClose={onClose}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="Nom de la liste">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Newsletter Fondateurs" style={inputStyle} autoFocus maxLength={120} />
                </Field>
                <Field label="Description (optionnelle)">
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="À qui s'adresse cette liste ?" rows={3} style={{ ...inputStyle, resize: 'vertical' }} maxLength={500} />
                </Field>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: palette.muted, lineHeight: 1.55, cursor: 'pointer' }}>
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 3 }} />
                    <span>
                        Je certifie disposer du consentement RGPD pour les contacts ajoutés à cette liste,
                        et m'engage à ne pas envoyer de message non sollicité.
                    </span>
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                    <button onClick={onClose} style={ghostBtn} disabled={submitting}>Annuler</button>
                    <button onClick={submit} style={accentBtn} disabled={submitting || !name.trim() || !consent}>
                        {submitting ? 'Création…' : 'Créer la liste'}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

// ═══════════════════════════════════════════════════════════════
// Onglet Contacts (par liste)
// ═══════════════════════════════════════════════════════════════
function SubscribersTab({ listId, onBack, listName }) {
    const [data, setData] = useState({ results: [], total: 0, page: 1 });
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // '' | 'enabled' | 'disabled' | 'blocklisted'
    const [showAdd, setShowAdd] = useState(false);
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const handleExportCSV = useCallback(async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const { filename } = await mailingApi.exportSubscribersCSV(listId);
            toast.success(`Export téléchargé : ${filename}`);
        } catch (err) {
            handleApiError(err);
        } finally {
            setExporting(false);
        }
    }, [listId, exporting]);

    const load = useCallback(async (page = 1, q = '', status = '') => {
        setLoading(true);
        try {
            const out = await mailingApi.listSubscribers(listId, { page, perPage: 50, q, status });
            setData({ ...out, page });
            setSelectedEmails(new Set()); // reset sélection après reload
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    }, [listId]);

    useEffect(() => { load(1, query, statusFilter); /* eslint-disable-line */ }, [listId, statusFilter]);

    const handleSearch = () => load(1, query, statusFilter);

    const toggleOne = (email) => {
        setSelectedEmails(prev => {
            const next = new Set(prev);
            if (next.has(email)) next.delete(email);
            else next.add(email);
            return next;
        });
    };
    const toggleAll = () => {
        if (selectedEmails.size === data.results.length) {
            setSelectedEmails(new Set());
        } else {
            setSelectedEmails(new Set(data.results.map(s => s.email)));
        }
    };
    const clearSelection = () => setSelectedEmails(new Set());

    const handleBulkAction = async (action) => {
        const emails = [...selectedEmails];
        if (emails.length === 0) return;
        const isDelete = action === 'delete';
        const label = isDelete ? 'Supprimer définitivement' : 'Retirer';
        const verb = isDelete ? 'supprimés définitivement de listmonk' : 'retirés de cette liste';
        const detail = isDelete
            ? `Cette action est irréversible. Les contacts seront effacés de listmonk (et de toutes les listes où ils figurent).`
            : `Les contacts restent dans listmonk et peuvent être ré-ajoutés plus tard.`;
        const ok = await confirmModal(
            `${label} ${emails.length} contact${emails.length > 1 ? 's' : ''} ?\n\n${detail}`,
            { confirmLabel: label, danger: true }
        );
        if (!ok) return;
        setBulkBusy(true);
        try {
            const out = await mailingApi.bulkSubscriberAction(listId, { emails, action });
            const count = isDelete ? out.deleted : out.removed;
            toast.success(`${count} contact${count > 1 ? 's' : ''} ${verb}.`);
            load(1, query, statusFilter);
        } catch (err) {
            handleApiError(err);
        } finally {
            setBulkBusy(false);
        }
    };

    const handleRemove = async (email) => {
        const ok = await confirmModal(`Retirer ${email} de la liste ?`, { confirmLabel: 'Retirer', danger: true });
        if (!ok) return;
        try {
            await mailingApi.removeSubscriber(listId, email);
            toast.success('Abonné retiré.');
            load(data.page, query, statusFilter);
        } catch (err) {
            handleApiError(err);
        }
    };

    const allChecked = data.results.length > 0 && selectedEmails.size === data.results.length;
    const someChecked = selectedEmails.size > 0 && selectedEmails.size < data.results.length;

    return (
        <div style={{ position: 'relative', paddingBottom: selectedEmails.size > 0 ? 60 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <button onClick={onBack} style={iconBtn} title="Retour aux listes">
                    <ArrowLeft size={13} />
                </button>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: palette.text }}>
                    Contacts <span style={{ color: palette.muted, fontWeight: 500 }}>({data.total})</span>
                </span>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: palette.muted, pointerEvents: 'none' }} />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder="Rechercher un email…"
                            style={{ ...inputStyle, paddingLeft: 30, width: 200, fontSize: 13 }}
                        />
                    </div>
                    <div style={{ position: 'relative' }}>
                        <Filter size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: palette.muted, pointerEvents: 'none' }} />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ ...inputStyle, paddingLeft: 28, paddingRight: 24, fontSize: 12.5, cursor: 'pointer', appearance: 'none', width: 145 }}
                        >
                            <option value="">Tous statuts</option>
                            <option value="enabled">Activés</option>
                            <option value="disabled">Désactivés</option>
                            <option value="blocklisted">Bloqués</option>
                        </select>
                    </div>
                    <button onClick={handleExportCSV} style={ghostBtn} title="Exporter la liste en CSV" disabled={exporting || data.total === 0}>
                        <Download size={13} /> {exporting ? 'Export…' : 'Exporter'}
                    </button>
                    <button onClick={() => setShowBulkImport(true)} style={ghostBtn} title="Importer un fichier CSV">
                        <Upload size={13} /> Importer
                    </button>
                    <button onClick={() => setShowHistory(true)} style={ghostBtn} title="Historique des imports CSV / Apify / N8N">
                        <History size={13} /> Historique
                    </button>
                    <button onClick={() => setShowAdd(true)} style={accentBtn}>
                        <Plus size={13} /> Ajouter
                    </button>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : data.results.length === 0 ? (
                <EmptyState
                    title={statusFilter ? "Aucun contact pour ce filtre" : "Aucun abonné dans cette liste"}
                    cta={statusFilter ? "Voir tous les contacts" : "Ajouter un premier contact"}
                    onCta={statusFilter ? () => setStatusFilter('') : () => setShowAdd(true)}
                />
            ) : (
                <>
                    {/* En-tête tableau : Tout / Aucun */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '6px 14px', marginBottom: 6,
                        fontSize: 11, color: palette.muted, letterSpacing: 0.4, textTransform: 'uppercase',
                    }}>
                        <PremiumCheckbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={toggleAll}
                            ariaLabel="Tout sélectionner"
                        />
                        <span style={{ fontWeight: 600, color: selectedEmails.size > 0 ? palette.accent : palette.muted }}>
                            {selectedEmails.size > 0
                                ? `${selectedEmails.size} sélectionné${selectedEmails.size > 1 ? 's' : ''}`
                                : `${data.results.length} sur la page`}
                        </span>
                        <div style={{ flex: 1 }} />
                        <span style={{ fontWeight: 500 }}>Email</span>
                        <span style={{ fontWeight: 500, width: 90, textAlign: 'right' }}>Statut</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {data.results.map((s, idx) => {
                            const isSelected = selectedEmails.has(s.email);
                            return (
                                <div
                                    key={s.id || idx}
                                    className="mailing-row-host"
                                    onClick={(e) => {
                                        // clic sur la ligne (hors boutons) toggle la sélection
                                        if (e.target.closest('button')) return;
                                        toggleOne(s.email);
                                    }}
                                    style={{
                                        background: isSelected ? palette.accentBg : palette.cardBg,
                                        border: `1px solid ${isSelected ? palette.accentBorder : palette.cardBorder}`,
                                        borderRadius: 10,
                                        padding: '12px 14px',
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        cursor: 'pointer',
                                        transition: 'all 0.12s ease',
                                    }}
                                >
                                    <PremiumCheckbox
                                        checked={isSelected}
                                        onChange={() => toggleOne(s.email)}
                                        ariaLabel={`Sélectionner ${s.email}`}
                                    />
                                    <div style={{
                                        width: 28, height: 28, borderRadius: 7,
                                        background: palette.accentBg,
                                        border: `1px solid ${palette.accentBorder}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <Mail size={12} style={{ color: palette.accent }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13.5, fontWeight: 500, color: palette.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.email}</div>
                                        {s.name && s.name !== s.email.split('@')[0] && (
                                            <div style={{ fontSize: 11.5, color: palette.muted, marginTop: 1 }}>{s.name}</div>
                                        )}
                                    </div>
                                    <SubscriberStatusChip status={s.status} />
                                    <button onClick={(e) => { e.stopPropagation(); handleRemove(s.email); }} className="mailing-row-trash" style={{ ...iconBtn, padding: 6, color: palette.danger, borderColor: 'transparent' }} title="Retirer">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Barre actions sticky bottom — premium */}
            {selectedEmails.size > 0 && (
                <div className="mailing-bulk-bar" style={{
                    position: 'sticky', bottom: 0, marginTop: 16,
                    background: 'linear-gradient(180deg, rgba(167,139,250,0.04) 0%, rgba(167,139,250,0.10) 100%)',
                    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                    border: `1px solid ${palette.accentBorder}`,
                    borderRadius: 12, padding: '10px 14px',
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    boxShadow: '0 8px 24px rgba(167,139,250,0.18)',
                }}>
                    <div style={{
                        width: 26, height: 26, borderRadius: 7,
                        background: palette.accentBg,
                        border: `1px solid ${palette.accentBorder}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Check size={13} style={{ color: palette.accent }} />
                    </div>
                    <span style={{ fontSize: 13, color: palette.text, fontWeight: 600 }}>
                        {selectedEmails.size} contact{selectedEmails.size > 1 ? 's' : ''} sélectionné{selectedEmails.size > 1 ? 's' : ''}
                    </span>
                    <div style={{ flex: 1 }} />
                    <button onClick={clearSelection} style={ghostBtn} disabled={bulkBusy}>
                        Annuler
                    </button>
                    <button
                        onClick={() => handleBulkAction('remove')}
                        style={{ ...ghostBtn, color: palette.warning, borderColor: 'rgba(251,191,36,0.30)' }}
                        disabled={bulkBusy}
                        title="Retire de cette liste, conserve dans listmonk"
                    >
                        <ArrowLeft size={13} /> Retirer de la liste
                    </button>
                    <button
                        onClick={() => handleBulkAction('delete')}
                        style={{ ...ghostBtn, color: palette.danger, borderColor: 'rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.06)' }}
                        disabled={bulkBusy}
                        title="Supprime définitivement de listmonk"
                    >
                        <Trash2 size={13} /> Supprimer définitivement
                    </button>
                </div>
            )}

            {showAdd && (
                <AddSubscriberModal
                    listId={listId}
                    onClose={() => setShowAdd(false)}
                    onAdded={() => { setShowAdd(false); load(data.page, query); }}
                />
            )}

            {showBulkImport && (
                <BulkImportModal
                    listId={listId}
                    listName={listName}
                    onClose={() => setShowBulkImport(false)}
                    onImported={() => load(1, query)}
                />
            )}

            {showHistory && (
                <ImportHistoryModal
                    listId={listId}
                    listName={listName}
                    onClose={() => setShowHistory(false)}
                />
            )}
        </div>
    );
}

function ImportHistoryModal({ listId, listName, onClose }) {
    const [imports, setImports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(() => new Set());

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const out = await mailingApi.listBulkImports(listId, { limit: 30 });
                if (alive) setImports(out.imports || []);
            } catch (err) {
                if (alive) handleApiError(err);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [listId]);

    const toggleExpand = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const fmtDate = (d) => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    const fmtDuration = (ms) => ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
    const sourceLabel = { csv: 'CSV', apify: 'Apify', n8n: 'N8N', manual: 'Manuel' };
    const statusColor = { completed: palette.success, running: palette.warning, failed: palette.danger, cancelled: palette.muted };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: palette.panel, border: `1px solid ${palette.border}`, borderRadius: 14, width: 720, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <header style={{ padding: '18px 22px', borderBottom: `1px solid ${palette.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <History size={18} style={{ color: palette.accent }} />
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: palette.text, flex: 1 }}>
                        Historique imports — <span style={{ color: palette.muted, fontWeight: 500 }}>{listName}</span>
                    </h2>
                    <button onClick={onClose} style={{ ...iconBtn, padding: 6 }} title="Fermer"><X size={16} /></button>
                </header>
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 22px' }}>
                    {loading ? <Loading /> : imports.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: palette.muted, fontSize: 13 }}>
                            Aucun import effectué sur cette liste.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {imports.map(imp => {
                                const isExpanded = expanded.has(imp.id);
                                const hasErrors = Array.isArray(imp.errors) && imp.errors.length > 0;
                                return (
                                    <article key={imp.id} style={{ background: palette.cardBg, border: `1px solid ${palette.cardBorder}`, borderRadius: 10, padding: '12px 14px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: palette.accentSoft, color: palette.accent, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                                                {sourceLabel[imp.source] || imp.source}
                                            </span>
                                            <span style={{ fontSize: 12, color: palette.muted }}>{fmtDate(imp.started_at)}</span>
                                            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 999, background: 'transparent', border: `1px solid ${statusColor[imp.status] || palette.muted}`, color: statusColor[imp.status] || palette.muted, fontWeight: 600 }}>
                                                {imp.status}
                                            </span>
                                            <div style={{ flex: 1 }} />
                                            <span style={{ fontSize: 11.5, color: palette.muted }}>{fmtDuration(imp.duration_ms)}</span>
                                        </div>
                                        <div style={{ marginTop: 8, display: 'flex', gap: 18, fontSize: 12, color: palette.text, flexWrap: 'wrap' }}>
                                            <span><strong>{imp.total}</strong> total</span>
                                            <span style={{ color: palette.success }}><strong>{imp.created}</strong> créés</span>
                                            <span style={{ color: palette.muted }}><strong>{imp.attached_existing}</strong> attachés</span>
                                            <span style={{ color: palette.warning }}><strong>{imp.skipped}</strong> ignorés</span>
                                            {hasErrors && (
                                                <button onClick={() => toggleExpand(imp.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: palette.danger, fontSize: 11.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    <AlertCircle size={12} /> {imp.errors.length} erreur{imp.errors.length > 1 ? 's' : ''} {isExpanded ? '▲' : '▼'}
                                                </button>
                                            )}
                                        </div>
                                        {isExpanded && hasErrors && (
                                            <div style={{ marginTop: 10, padding: 10, background: palette.dangerBg, borderRadius: 6, fontSize: 11.5, color: palette.danger, fontFamily: 'monospace', maxHeight: 180, overflowY: 'auto' }}>
                                                {imp.errors.slice(0, 50).map((e, i) => (
                                                    <div key={i} style={{ marginBottom: 4 }}>
                                                        {typeof e === 'string' ? e : `${e.email || '—'} · ${e.reason || e.message || JSON.stringify(e)}`}
                                                    </div>
                                                ))}
                                                {imp.errors.length > 50 && <div style={{ fontStyle: 'italic', marginTop: 6 }}>… et {imp.errors.length - 50} autres</div>}
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SubscriberStatusChip({ status }) {
    const conf = status === 'enabled'
        ? { label: 'confirmé', color: palette.success, bg: palette.successBg }
        : status === 'blocklisted' || status === 'disabled'
            ? { label: status === 'blocklisted' ? 'bloqué' : 'désactivé', color: palette.danger, bg: palette.dangerBg }
            : { label: status || 'en attente', color: palette.warning, bg: palette.warningBg };
    return (
        <span style={{
            fontSize: 10, padding: '3px 8px', borderRadius: 999,
            background: conf.bg, color: conf.color, fontWeight: 600,
            letterSpacing: 0.4, textTransform: 'uppercase',
            border: `1px solid ${conf.color}33`,
        }}>
            {conf.label}
        </span>
    );
}

function AddSubscriberModal({ listId, onClose, onAdded }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [skipDoubleOptin, setSkipDoubleOptin] = useState(true); // par défaut : Soufari maîtrise ses contacts
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            const out = await mailingApi.addSubscriber(listId, {
                email: email.trim(),
                name: name.trim() || null,
                skipDoubleOptin,
            });
            const action = out?.status === 'created' ? 'créé' : 'rattaché à la liste';
            const tone = skipDoubleOptin ? 'consentement attesté' : 'double opt-in';
            toast.success(`Contact ${action} (${tone}).`);
            onAdded();
        } catch (err) {
            console.error('[Mailing] addSubscriber error', err);
            handleApiError(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalShell title="Ajouter un contact" onClose={onClose}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="Email">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && email.trim() && !submitting) submit(); }}
                        placeholder="contact@exemple.com"
                        style={inputStyle}
                        autoFocus
                    />
                </Field>
                <Field label="Nom (optionnel)">
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && email.trim() && !submitting) submit(); }}
                        placeholder="Prénom Nom"
                        style={inputStyle}
                    />
                </Field>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 12.5, color: palette.muted, lineHeight: 1.55 }}>
                    <input
                        type="checkbox"
                        checked={skipDoubleOptin}
                        onChange={(e) => setSkipDoubleOptin(e.target.checked)}
                        style={{ marginTop: 3, accentColor: palette.accent }}
                    />
                    <span>
                        <strong style={{ color: palette.text }}>Le contact a déjà donné son consentement explicite.</strong>{' '}
                        Cocher = abonnement direct, pas d'email de confirmation. Décocher = double opt-in
                        (un email demande au contact de confirmer son inscription).
                    </span>
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                    <button onClick={onClose} style={ghostBtn} disabled={submitting}>Annuler</button>
                    <button onClick={submit} style={accentBtn} disabled={submitting || !email.trim()}>
                        {submitting ? 'Ajout…' : 'Ajouter'}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

// ═══════════════════════════════════════════════════════════════
// Onglet Campagnes
// ═══════════════════════════════════════════════════════════════
function CampaignsTab({ onCompose }) {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { campaigns } = await mailingApi.listCampaigns({ limit: 100 });
            setCampaigns(campaigns || []);
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Poll silencieux (sans loader) toutes les 30s tant qu'il y a au moins
    // une campagne en cours d'envoi ou programmée. Sinon on laisse tranquille.
    const silentLoad = useCallback(async () => {
        try {
            const { campaigns } = await mailingApi.listCampaigns({ limit: 100 });
            setCampaigns(campaigns || []);
        } catch (_) { /* swallow — un retry automatique au prochain tick */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const hasLive = campaigns.some(c => c.status === 'sending' || c.status === 'scheduled');
        if (!hasLive) return undefined;
        const id = setInterval(silentLoad, 30000);
        return () => clearInterval(id);
    }, [campaigns, silentLoad]);

    const handleDelete = async (c) => {
        const ok = await confirmModal(
            `Supprimer la campagne « ${c.subject} » ? Le brouillon sera retiré de votre liste (action réversible côté DB seulement).`,
            { confirmLabel: 'Supprimer', danger: true }
        );
        if (!ok) return;
        try {
            await mailingApi.deleteCampaign(c.id);
            toast.success('Campagne supprimée.');
            load();
        } catch (err) {
            handleApiError(err);
        }
    };

    return (
        <div>
            <div style={sectionHeader}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: palette.text }}>
                    Mes campagnes <span style={{ color: palette.muted, fontWeight: 500 }}>({campaigns.length})</span>
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={load} style={iconBtn} title="Rafraîchir"><RefreshCw size={13} /></button>
                    <button onClick={() => onCompose(null)} style={accentBtn}>
                        <Plus size={13} /> Nouvelle campagne
                    </button>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : campaigns.length === 0 ? (
                <EmptyState
                    title="Pas encore de campagne envoyée"
                    cta="Composer une première campagne"
                    onCta={() => onCompose(null)}
                />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {campaigns.map((c) => {
                        const editable = ['draft', 'test_sent', 'scheduled'].includes(c.status);
                        const deletable = ['draft', 'test_sent', 'cancelled'].includes(c.status);
                        const handleOpen = async () => {
                            if (!editable) return;
                            try {
                                const { campaign } = await mailingApi.getCampaign(c.id);
                                onCompose({
                                    listmonkListId: campaign.listmonk_list_id,
                                    listName: campaign.list_name,
                                    subject: campaign.subject,
                                    bodyMd: campaign.body_md,
                                    templateSlug: campaign.template_slug,
                                    senderName: campaign.sender_name,
                                    senderEmail: campaign.sender_email,
                                    replyTo: campaign.reply_to,
                                    scheduledAt: campaign.scheduled_at,
                                });
                            } catch (err) {
                                handleApiError(err);
                            }
                        };
                        return (
                            <article
                                key={c.id}
                                className="mailing-row-host"
                                onClick={handleOpen}
                                style={{
                                    background: palette.cardBg, border: `1px solid ${palette.cardBorder}`,
                                    borderRadius: 10, padding: 14,
                                    display: 'flex', alignItems: 'center', gap: 14,
                                    cursor: editable ? 'pointer' : 'default',
                                    transition: 'all 0.12s',
                                }}
                                onMouseEnter={editable ? (e) => { e.currentTarget.style.borderColor = palette.accentBorder; e.currentTarget.style.background = palette.accentSoft; } : undefined}
                                onMouseLeave={editable ? (e) => { e.currentTarget.style.borderColor = palette.cardBorder; e.currentTarget.style.background = palette.cardBg; } : undefined}
                                title={editable ? 'Cliquer pour rouvrir le brouillon' : ''}
                            >
                                <div style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    background: palette.accentBg,
                                    border: `1px solid ${palette.accentBorder}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Send size={13} style={{ color: palette.accent }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
                                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: palette.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {c.subject}
                                        </h3>
                                        <CampaignStatusChip status={c.status} />
                                    </div>
                                    <div style={{ fontSize: 11.5, color: palette.muted }}>
                                        {c.list_name && <>Liste : {c.list_name} · </>}
                                        Modèle : {c.template_slug}
                                        {c.sent_at && <> · Envoyé le {new Date(c.sent_at).toLocaleString('fr-FR')}</>}
                                        {!c.sent_at && c.created_at && <> · Brouillon du {new Date(c.created_at).toLocaleDateString('fr-FR')}</>}
                                        {editable && <> · <span style={{ color: palette.accent, fontWeight: 600 }}>Cliquer pour éditer</span></>}
                                    </div>
                                </div>
                                {c.recipients_count > 0 && (
                                    <div style={{ textAlign: 'right', fontSize: 11.5, color: palette.muted, lineHeight: 1.55 }}>
                                        <div>{c.recipients_count} envoyé{c.recipients_count > 1 ? 's' : ''}</div>
                                        {c.opened_count > 0 && <div>{c.opened_count} ouvert{c.opened_count > 1 ? 's' : ''}</div>}
                                        {c.clicked_count > 0 && <div>{c.clicked_count} clic{c.clicked_count > 1 ? 's' : ''}</div>}
                                    </div>
                                )}
                                {deletable && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                                        className="mailing-row-trash"
                                        style={{ ...iconBtn, padding: 6, color: palette.danger, borderColor: 'transparent', flexShrink: 0 }}
                                        title="Supprimer"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function CampaignStatusChip({ status }) {
    const map = {
        draft: { label: 'Brouillon', color: palette.muted, bg: palette.accentSoft },
        test_sent: { label: 'Test envoyé', color: palette.warning, bg: palette.warningBg },
        scheduled: { label: 'Programmée', color: palette.warning, bg: palette.warningBg },
        sending: { label: 'En cours', color: palette.accent, bg: palette.accentBg },
        sent: { label: 'Envoyée', color: palette.success, bg: palette.successBg },
        failed: { label: 'Échec', color: palette.danger, bg: palette.dangerBg },
        cancelled: { label: 'Annulée', color: palette.muted, bg: palette.accentSoft },
        paused: { label: 'En pause', color: palette.warning, bg: palette.warningBg },
    };
    const m = map[status] || map.draft;
    return (
        <span style={{
            fontSize: 9.5, padding: '3px 8px', borderRadius: 999,
            background: m.bg, color: m.color, fontWeight: 600,
            letterSpacing: 0.4, textTransform: 'uppercase',
            border: `1px solid ${m.color}33`,
        }}>
            {m.label}
        </span>
    );
}

// ═══════════════════════════════════════════════════════════════
// Onglet Quotas
// ═══════════════════════════════════════════════════════════════
function QuotasTab() {
    const [q, setQ] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const out = await mailingApi.getQuotas();
                setQ(out);
            } catch (err) {
                handleApiError(err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <Loading />;
    if (!q) return <p style={{ color: palette.muted, fontSize: 13 }}>Quotas indisponibles.</p>;

    const bars = [
        { label: 'Listes actives', used: q.used.lists, max: q.max_lists, hint: 'Combien de listes vous pouvez gérer simultanément' },
        { label: 'Campagnes ce mois', used: q.used.campaigns_this_month, max: q.max_campaigns_per_month, hint: 'Limite mensuelle, repart à zéro le 1er du mois' },
        { label: 'Mails envoyés aujourd\'hui', used: q.used.mails_today, max: q.max_mails_per_day, hint: 'Limite quotidienne sur tous mails sortants' },
    ];

    return (
        <div style={{ maxWidth: 720 }}>
            <div style={{ marginBottom: 18 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: palette.text }}>Vos quotas</span>
                <p style={{ margin: '4px 0 0 0', color: palette.muted, fontSize: 12.5, lineHeight: 1.55 }}>
                    Ces limites protègent votre délivrabilité et le serveur. Pour les augmenter, contactez l'administrateur.
                </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {bars.map((b) => {
                    const pct = Math.min(100, Math.round((b.used / b.max) * 100));
                    const color = pct >= 100 ? palette.danger : pct >= 80 ? palette.warning : palette.accent;
                    return (
                        <div key={b.label} style={{
                            background: palette.cardBg, border: `1px solid ${palette.cardBorder}`,
                            borderRadius: 10, padding: 14,
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                                <strong style={{ fontSize: 13.5, color: palette.text }}>{b.label}</strong>
                                <span style={{ fontSize: 12.5, color: palette.muted }}>
                                    <strong style={{ color }}>{b.used}</strong> / {b.max}
                                </span>
                            </div>
                            <div style={{ height: 6, background: palette.accentSoft, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 300ms ease' }} />
                            </div>
                            <p style={{ margin: '6px 0 0 0', fontSize: 11.5, color: palette.muted }}>{b.hint}</p>
                        </div>
                    );
                })}
                <div style={{
                    background: palette.cardBg, border: `1px solid ${palette.cardBorder}`,
                    borderRadius: 10, padding: 14,
                }}>
                    <strong style={{ fontSize: 13.5, color: palette.text }}>Abonnés par liste</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: 12.5, color: palette.muted }}>
                        Limite : <strong style={{ color: palette.text }}>{q.max_subscribers_per_list}</strong> abonnés par liste.
                    </p>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// Composants UI partagés
// ═══════════════════════════════════════════════════════════════
function ModalShell({ title, onClose, children }) {
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: 20,
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: palette.cardBg, color: palette.text,
                border: `1px solid ${palette.cardBorder}`, borderRadius: 14,
                width: '100%', maxWidth: 460, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
            }}>
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: palette.text }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: palette.muted, padding: 4 }}>
                        <X size={16} />
                    </button>
                </header>
                {children}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: palette.muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</span>
            {children}
        </label>
    );
}

function EmptyState({ title, cta, onCta }) {
    return (
        <div style={{
            background: palette.accentSoft, border: `1px dashed ${palette.accentBorder}`,
            borderRadius: 12, padding: '48px 24px', textAlign: 'center',
        }}>
            <Mail size={28} style={{ color: palette.accent, opacity: 0.7, marginBottom: 12 }} />
            <p style={{ margin: '0 0 16px 0', color: palette.muted, fontSize: 13.5 }}>{title}</p>
            {cta && <button onClick={onCta} style={accentBtn}>{cta}</button>}
        </div>
    );
}

function Loading() {
    return <div style={{ padding: 40, textAlign: 'center', color: palette.muted, fontSize: 13 }}>Chargement…</div>;
}

// Checkbox premium : span clickable pur (pas de label/input caché qui parasite
// la propagation). Accessible via role="checkbox" + Space/Enter clavier.
function PremiumCheckbox({ checked, indeterminate = false, onChange, ariaLabel }) {
    const filled = checked || indeterminate;
    const handleActivate = (e) => {
        if (e) {
            e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
        }
        onChange?.();
    };
    return (
        <span
            role="checkbox"
            aria-checked={indeterminate ? 'mixed' : checked}
            aria-label={ariaLabel}
            tabIndex={0}
            onClick={handleActivate}
            onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') handleActivate(e);
            }}
            style={{
                width: 16, height: 16, borderRadius: 4,
                border: `1.5px solid ${filled ? palette.accent : palette.cardBorder}`,
                background: filled ? palette.accent : 'transparent',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.12s ease',
                cursor: 'pointer', flexShrink: 0,
                outline: 'none', userSelect: 'none',
            }}
        >
            {checked && <Check size={11} style={{ color: '#fff', strokeWidth: 3 }} />}
            {indeterminate && !checked && (
                <span style={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />
            )}
        </span>
    );
}

const sectionHeader = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, gap: 10, flexWrap: 'wrap',
};

// ═══════════════════════════════════════════════════════════════
// Helpers UI — pattern Coffre (violet doux outline)
// ═══════════════════════════════════════════════════════════════
const accentBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: palette.accentBg,
    border: `1px solid ${palette.accentBorder}`, borderRadius: 6,
    padding: '7px 12px',
    color: palette.accent, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 150ms ease',
};

const ghostBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'transparent', color: palette.muted,
    border: `1px solid ${palette.cardBorder}`, borderRadius: 6,
    padding: '7px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const iconBtn = {
    background: 'transparent', border: `1px solid ${palette.cardBorder}`,
    borderRadius: 6, padding: 7, cursor: 'pointer',
    color: palette.muted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 150ms ease',
};

const inputStyle = {
    background: palette.bg, color: palette.text,
    border: `1px solid ${palette.cardBorder}`, borderRadius: 8,
    padding: '9px 12px', fontSize: 13.5,
    fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box',
};

function handleApiError(err) {
    if (err instanceof MailingApiError) {
        const code = err.code || '';
        const messages = {
            quota_exceeded: 'Limite atteinte — voir l\'onglet Quotas.',
            consent_missing: 'Le consentement RGPD est requis.',
            invalid_email: 'Email invalide.',
            subscriber_blacklisted: 'Domaine d\'email refusé (jetable / spam).',
            not_owner: 'Cette ressource ne vous appartient pas.',
            config_missing: 'Le module mailing n\'est pas encore configuré côté serveur.',
            unreachable: 'Le serveur de mailing ne répond pas.',
            invalid_credentials: 'Authentification listmonk refusée — contactez l\'admin.',
            invalid_template: 'Modèle de campagne inconnu.',
        };
        toast.error(messages[code] || err.message || 'Erreur serveur.');
    } else {
        toast.error(err.message || 'Erreur réseau.');
    }
}

export default MailingPage;
